import React, { useEffect, useState } from "react";
import V1BatchExportManager from "../../../managers/V1BatchExportManager";
import { Supervisor } from "../../../interfaces/V1Interface";
import "./V1BatchExport.css";
import Colors from '../../../styles/Colors.json';
import { useSiteContext } from "../../../SiteContext";
import PageWrapper from "../../../components/PageWrapper";
import SearchBar from "../../../components/SearchBar";
import BatchExportModal from "../../../components/BatchExportModal";

// Define the props interface
interface V1BatchExportProps {
    setError: (error: string) => void;
  }
  
  const V1BatchExport: React.FC<V1BatchExportProps> = ({ setError }) => {
  const {
    startDate,
    endDate,
    translator,
    setLoading,
    createPopup,
  } = useSiteContext();

  // State for supervisors and search term
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedSupervisorName, setSelectedSupervisorName] = useState("");
  const [selectedExportType, setSelectedExportType] = useState<"paint" | "drywall" | null>(null);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState("");


    // When export button clicked
    const handleExportClick = (supervisor: Supervisor, type: "paint" | "drywall") => {
        setSelectedSupervisorName(supervisor.supervisorName || "");
        setSelectedSupervisorId(supervisor.supervisorID || "");
        setSelectedExportType(type);
        setModalVisible(true);
      };      

    // When print timecards button clicked
    const handlePrintTimecards = async (supervisor: Supervisor) => {
        try {
            setLoading(true);
            
            // Format dates as YYYY-MM-DD
            const formattedStartDate = startDate.split('T')[0];
            const formattedEndDate = endDate.split('T')[0];
            
            // Call the API to generate the PDF
            const pdfPath = await V1BatchExportManager.printTimecards(
                supervisor.supervisorID || "",
                formattedStartDate,
                formattedEndDate
            );
            
            // Download the PDF
            const downloadUrl = `https://admin.api.wcdp.com${pdfPath.replace(/^\//, '')}`;
            
            // Open the PDF in a new tab
            window.open(downloadUrl, '_blank');
            
            // Show success popup
            createPopup("Timecards Generated Successfully!", "The PDF file has been opened in a new tab.");
            
        } catch (error) {
            console.error("Error printing timecards:", error);
            createPopup("Error Generating Timecards", "An error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };


  // Fetch supervisors for the current date range
  const fetchSupervisors = async () => {
    try {
      setLoading(true);
      const timeSheetDate = endDate;
      const data = await V1BatchExportManager.getSupervisors(timeSheetDate, startDate, endDate);
      
      // Add validation to ensure data is an array
      if (!Array.isArray(data)) {
        console.error("Expected array but got:", data);
        setSupervisors([]); // Fallback to empty array
        setError("Invalid data format received from server");
        return;
      }
      
      setSupervisors(data);
    } catch (error) {
      console.error("Error fetching supervisors:", error);
      setError("Failed to fetch supervisors. Please try again later.");
      setSupervisors([]); // Fallback to empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when date range changes
  useEffect(() => {
    if (startDate && endDate) {
      fetchSupervisors();
    }
  }, [startDate, endDate]);

  // Filter supervisors based on search term
  const filteredSupervisors = Array.isArray(supervisors) 
  ? supervisors.filter(supervisor => {
      if (!supervisor) return false; // Skip if supervisor is null/undefined
      
      const searchLower = searchTerm.toLowerCase();
      const name = supervisor.supervisorName?.toLowerCase() || '';
      const id = supervisor.supervisorID?.toLowerCase() || '';
      
      return (
        name.includes(searchLower) ||
        id.includes(searchLower)
      );
    })
  : [];

  // Handle supervisor selection
  const handleSupervisorSelect = (supervisor: Supervisor) => {
    console.log("Selected supervisor:", supervisor);
    // Here you would typically navigate to another page or open a modal
    // to handle the batch export process for this supervisor
  };

  return (
    <>
      <PageWrapper 
        title={translator.translate("v1BatchExport")} 
        backgroundColor={Colors.White} 
        backArrow={true}
        weekSelector={true}
      >
        <div className="batch-container">
          {/* Search bar */}
          <SearchBar
            placeholder={"Search Supervisors"}
            value={searchTerm}
            onChange={setSearchTerm}
            style={{ width: '75%', marginBottom: '20px' }}
          />
  
          {/* Supervisors list */}
          <div className="batch-list">
            {filteredSupervisors.length > 0 ? (
              filteredSupervisors.map(supervisor => (
                <div 
                  key={supervisor.supervisorID}
                  className="batch-card"
                  onClick={() => handleSupervisorSelect(supervisor)}
                >
                  <div className="batch-info">
                    <h4>{supervisor.supervisorName}</h4>
                    <p>ID: {supervisor.supervisorID}</p>
                  </div>
                  <div className="batch-actions">
                    <div className="batch-actions-row">
                      <button 
                        className="batch-button batch-paint"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportClick(supervisor, "paint");
                        }}
                      >
                        Export Paint
                      </button>
                      <button 
                        className="batch-button batch-drywall"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExportClick(supervisor, "drywall");
                        }}
                      >
                        Export Drywall
                      </button>
                    </div>
                    <div className="batch-actions-row">
                      <button 
                        className="batch-button batch-print"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePrintTimecards(supervisor);
                        }}
                        style={{ marginLeft: 'auto' }}
                      >
                        Print V1 Timecards
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="batch-no-supervisors">{"No Supervisors Found"}</p>
            )}
          </div>
        </div>
      </PageWrapper>
  
      {/* Modal */}
      <BatchExportModal 
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        supervisorName={selectedSupervisorName}
        supervisorId={selectedSupervisorId}
        exportType={selectedExportType || "paint"}
        startDate={startDate}
        endDate={endDate}
      />
    </>
  );  
};

export default V1BatchExport;